import React from 'react';
import './index.css';
import { BattleshipGameState, GameInstance } from '../../../../types/types';
import useBattleshipGamePage from '../../../../hooks/useBattleshipGamePage';
import BattleshipTile from './battleshipTile';

/**
 * Component to display the "Battleship" game page, including the rules, game details, and functionality to make a move.
 * @param gameInstance The current instance of the Battleship game, including player details, game status, and boards.
 * @returns A React component that shows:
 * - The rules of the Battleship game.
 * - The current game details, such as players, current turn, boards, and winner (if the game is over).
 * - The current move selected and a submit button to finalize the move.
 */
const BattleshipGamePage = ({
  gameInstance,
}: {
  gameInstance: GameInstance<BattleshipGameState>;
}) => {
  const { user, move, handleMakeMove, handleInputChange } = useBattleshipGamePage(gameInstance);
  /**
   * Renders the appropriate game component based on the game type.
   * @param gameType The type of the game to render (e.g., "Battleship").
   * @returns A React component corresponding to the specified game type, or a
   * fallback message for unknown types.
   */
  const renderBoardComponents = () => {
    let myBoard;
    let opponentBoard;
    if (gameInstance.state.player1 === user.username) {
      myBoard = gameInstance.state.player1Board;
      opponentBoard = gameInstance.state.player2Board;
    } else {
      myBoard = gameInstance.state.player2Board;
      opponentBoard = gameInstance.state.player1Board;
    }

    return (
      <div>
        <div>
          <p>Your board</p>
          <div className='battleship-game-board'>
            {myBoard?.map((tileRow, i) => (
              <div className='battleship-game-board-row' key={`my-board-row-${i}`}>
                {tileRow.map((tileState, j) => (
                  <BattleshipTile
                    key={`my-board-${i}-${j}`}
                    state={tileState}
                    isOpponentBoard={false}
                    handleClick={handleInputChange}
                    tileMove={{ x: i, y: j }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div>
          <p>{`Opponent's board (Make moves here!)`}</p>
          <div className='battleship-game-board'>
            {opponentBoard?.map((tileRow, i) => (
              <div className='battleship-game-board-row' key={`opponent-board-row-${i}`}>
                {tileRow.map((tileState, j) => (
                  <BattleshipTile
                    key={`opponent-board-${i}-${j}`}
                    state={tileState}
                    isOpponentBoard={true}
                    handleClick={handleInputChange}
                    tileMove={{ x: i, y: j }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      <div>
        <h2>Rules of Battleship</h2>
        <p>The game of Battleship is played as follows:</p>
        <ol>
          <li>The game starts with ships placed on random spaces on each players&apos; boards.</li>
          <li>
            Players take turns selecting coordinates to shoot a shot on their opponent&apos;s board.
          </li>
          <li>A missed shot will appear as an Ｘ while a hit will appear as a 🔥.</li>
          <li>The player who destroys all of their opponent&apos;s ships wins.</li>
        </ol>
        <p>
          Think strategically and try to really think about where your opponent&apos;s boats might
          be!
        </p>
      </div>
      <div className='battleship-game-details'>
        <h2>Current Game</h2>
        <p>
          <strong>Player 1:</strong> {gameInstance.state.player1 || 'Waiting...'}
        </p>
        <p>
          <strong>Player 2:</strong> {gameInstance.state.player2 || 'Waiting...'}
        </p>
        <p>
          <strong>Current Player to Move:</strong>{' '}
          {gameInstance.players[gameInstance.state.moves.length % 2]}
        </p>
        {gameInstance.state.status === 'OVER' && (
          <p>
            <strong>Winner:</strong> {gameInstance.state.winners?.join(', ') || 'No winner'}
          </p>
        )}
        {gameInstance.state.status === 'IN_PROGRESS' && (
          <div className='battleship-game-move'>
            {renderBoardComponents()}
            <div>
              <p>
                <strong>Selected move:</strong>{' '}
                {typeof move === 'string' ? 'none' : `(${move.x},${move.y})`}
              </p>
              <button
                className='btn-submit'
                onClick={handleMakeMove}
                disabled={
                  gameInstance.players[gameInstance.state.moves.length % 2] !== user.username
                }>
                Submit Move
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BattleshipGamePage;
